import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { usePropertyScopeItems } from 'hooks/api/usePropertyScope';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import IssueInvoicesModal, { inHouseActions, invoiceActions, openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import NoteModal from 'components/modals/modules/pms-module/NoteModal';
import FolioModal from 'components/modals/modules/pms-module/FolioModal';
import notificationService from 'services/notificationService';
import { propertyService } from 'api/services/propertyService';
import { invoiceService } from 'api/services/invoiceService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import { formatCurrency } from 'utils/currencyFormat';
import { DOCUMENT_KIND_LABEL, INVOICE_ACTIONS, PAYER_LABEL, type InvoiceListRow, type StayToInvoice } from 'api/types/invoice.types';

/**
 * ============================================================================
 * PMS → INVOICES (PMS Phase 2 slice 7a)
 * ============================================================================
 * To invoice: checked-out stays with folio lines still to bill (the invoices
 * are normally issued at checkout; this catches what was left). Issued: every
 * checkout invoice of the property — one per payer of a stay — to view and
 * print. Permission module pms-invoices (view / issue / export).
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

type Section = 'pending' | 'issued';
type PendingRow = StayToInvoice & { id: string };

const day = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };

const PAYER_OPTIONS: SelectOption[] = [
  { value: '', label: 'Every payer' },
  ...Object.entries(PAYER_LABEL).map(([value, label]) => ({ value, label })),
];

const InvoicesScreen: React.FC<Props> = ({ tabId }) => {
  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const propertyLabel = usePropertyLabel(propertyId);
  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(iso(new Date()));
  const [payer, setPayer] = useState('');
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [open, setOpen] = useState<Section | null>('issued');
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [issued, setIssued] = useState<InvoiceListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);
  const [folioFor, setFolioFor] = useState<string | null>(null);
  /** A credit / debit note against this invoice (slice 7b). */
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const actions = invoiceActions();
  const canNote = actions.includes(INVOICE_ACTIONS.creditNote) || actions.includes(INVOICE_ACTIONS.debitNote);

  const load = useCallback(async () => {
    if (!propertyId) { setPending([]); setIssued([]); return; }
    setLoading(true);
    try {
      const [p, i] = await Promise.all([
        invoiceService.toInvoice(propertyId),
        invoiceService.list({ propertyId, from, to, billToType: payer || undefined, search: search.trim() || undefined, pageSize: 2000 }),
      ]);
      setPending(p.map((r) => ({ ...r, id: r.bookingId })));
      setIssued(i);
      if (p.length > 0 && open === null) setOpen('pending');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Invoices did not load.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, from, to, payer, search, open]);
  useEffect(() => { void load(); }, [load]);

  const pendingColumns = useMemo<SyutGridColumn<PendingRow>[]>(() => [
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 140, cell: ({ row }) => <b>{row.original.bookingNumber}</b> },
    { id: 'guestName', header: 'Guest', accessorKey: 'guestName', size: 180, cell: ({ row }) => row.original.guestName || '—' },
    { id: 'roomNumbers', header: 'Rooms', accessorKey: 'roomNumbers', size: 100, cell: ({ row }) => row.original.roomNumbers || '—' },
    { id: 'checkOutDate', header: 'Stay', accessorKey: 'checkOutDate', size: 200, cell: ({ row }) => `${day(row.original.checkInDate)} → ${day(row.original.checkOutDate)}` },
    { id: 'openLines', header: 'To bill', accessorKey: 'openLines', size: 150, cell: ({ row }) => `${row.original.openLines} line(s) · ${formatCurrency(row.original.openAmount)}` },
    {
      id: 'guestBalance', header: 'Guest owes', accessorKey: 'guestBalance', size: 120,
      cell: ({ row }) => <span style={{ color: row.original.guestBalance > 0 ? 'var(--danger, #dc2626)' : undefined }}>{formatCurrency(row.original.guestBalance)}</span>,
    },
    {
      id: 'actions', header: '', accessorKey: 'bookingId', size: 130,
      cell: ({ row }) => (
        <button className="btn btn-primary btn-sm" onClick={() => setInvoiceFor(row.original.bookingId)}>
          <IconSystem.file size={13} /> Invoices
        </button>
      ),
    },
  ], []);

  const issuedColumns = useMemo<SyutGridColumn<InvoiceListRow>[]>(() => [
    {
      id: 'invoiceNumber', header: 'Document', accessorKey: 'invoiceNumber', size: 170,
      cell: ({ row }) => {
        const r = row.original;
        const note = r.documentKind && r.documentKind !== 'INVOICE';
        return (
          <span>
            <span style={{ fontFamily: 'monospace' }}>{r.invoiceNumber}</span>
            {note && <span className={`badge ${r.documentKind === 'CREDIT_NOTE' ? 'badge-info' : 'badge-warning'}`} style={{ fontSize: '0.58rem', marginLeft: 4 }}>{DOCUMENT_KIND_LABEL[r.documentKind!]}</span>}
            {note && r.originalInvoiceNumber && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>against {r.originalInvoiceNumber}</div>}
            {!note && (!!r.creditedAmount || !!r.debitedAmount) && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {r.creditedAmount ? `credited ${formatCurrency(r.creditedAmount)}` : ''}{r.creditedAmount && r.debitedAmount ? ' · ' : ''}{r.debitedAmount ? `debited ${formatCurrency(r.debitedAmount)}` : ''}
              </div>
            )}
          </span>
        );
      },
    },
    { id: 'invoiceDate', header: 'Date', accessorKey: 'invoiceDate', size: 110, cell: ({ row }) => day(row.original.invoiceDate) },
    {
      id: 'billToName', header: 'Bill to', accessorKey: 'billToName', size: 220,
      cell: ({ row }) => (
        <span>{row.original.billToName}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {PAYER_LABEL[row.original.billToType] ?? row.original.billToType}{row.original.billToGstin ? ` · ${row.original.billToGstin}` : ''}
          </div>
        </span>
      ),
    },
    {
      id: 'bookingNumber', header: 'Stay', accessorKey: 'bookingNumber', size: 200,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber} · {row.original.roomNumbers || '—'}</div>
        </span>
      ),
    },
    { id: 'taxableAmount', header: 'Taxable', accessorKey: 'taxableAmount', size: 110, cell: ({ row }) => formatCurrency(row.original.taxableAmount) },
    { id: 'taxAmount', header: 'Tax', accessorKey: 'taxAmount', size: 100, cell: ({ row }) => formatCurrency(row.original.taxAmount) },
    {
      id: 'totalAmount', header: 'Total', accessorKey: 'totalAmount', size: 120,
      cell: ({ row }) => <b style={{ color: row.original.documentKind === 'CREDIT_NOTE' ? 'var(--danger, #dc2626)' : undefined }}>
        {formatCurrency(row.original.documentKind === 'CREDIT_NOTE' ? -row.original.totalAmount : row.original.totalAmount)}</b>,
    },
    { id: 'createdByName', header: 'By', accessorKey: 'createdByName', size: 120, cell: ({ row }) => row.original.createdByName || '—' },
  ], []);

  // The row's ⋮ menu: print, the stay, a credit / debit note (invoices only, by permission).
  const issuedActions = useCallback((row: InvoiceListRow): ActionMenuItem[] => {
    const isNote = (row.documentKind ?? 'INVOICE') !== 'INVOICE';
    const items: ActionMenuItem[] = [
      {
        id: 'print', label: isNote ? 'Print note' : 'Print invoice', icon: <IconSystem.print size={16} />,
        onClick: () => { void openPdf(() => invoiceService.pdf(row.id), 'The document did not print.'); },
      },
      {
        id: 'stay', label: 'Stay — invoices and statement', icon: <IconSystem.file size={16} />,
        onClick: () => setInvoiceFor(row.bookingId),
      },
    ];
    if (canNote && !isNote)
      items.push({
        id: 'note', label: 'Credit / debit note…', icon: <IconSystem.edit size={16} />,
        description: 'Reduce (credit, the default) or increase (debit) this invoice.',
        onClick: () => setNoteFor(row.id),
      });
    return items;
  }, [canNote]);

  // Net of notes: a credit note takes away, a debit note adds (slice 7b).
  const totals = useMemo(() => {
    const sign = (i: InvoiceListRow) => (i.documentKind === 'CREDIT_NOTE' ? -1 : 1);
    return {
      count: issued.length,
      taxable: issued.reduce((s, i) => s + sign(i) * i.taxableAmount, 0),
      tax: issued.reduce((s, i) => s + sign(i) * i.taxAmount, 0),
      total: issued.reduce((s, i) => s + sign(i) * i.totalAmount, 0),
    };
  }, [issued]);

  const section = (id: Section, title: string, count: number, tone: 'warning' | 'default', summary: React.ReactNode, body: React.ReactNode) => {
    const isOpen = open === id;
    return (
      <div key={id} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: isOpen ? '1 1 0' : '0 0 auto', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.75rem', background: 'var(--bg-secondary)', flexShrink: 0 }}>
          <button type="button" onClick={() => setOpen(isOpen ? null : id)} aria-expanded={isOpen}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: tone === 'warning' ? 'var(--warning-dark, #b45309)' : 'var(--text-primary)', fontWeight: 700, fontSize: '0.82rem' }}>
            {isOpen ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
            {title}
            <span className={`badge ${tone === 'warning' ? 'badge-warning' : 'badge-secondary'}`} style={{ fontSize: '0.65rem' }}>{count}</span>
          </button>
          <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{summary}</span>
        </div>
        {isOpen && <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{body}</div>}
      </div>
    );
  };

  const scopeItems = usePropertyScopeItems(propertyId);
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: `${day(from)} – ${day(to)}`, primary: true, title: 'Issued invoices by invoice date' },
    ...scopeItems,
    { label: 'Invoices', value: String(totals.count) },
    { label: 'Taxable', value: formatCurrency(totals.taxable) },
    { label: 'Tax', value: formatCurrency(totals.tax) },
    { label: 'Total', value: formatCurrency(totals.total) },
    ...(pending.length > 0 ? [{ label: 'To invoice', value: String(pending.length), icon: 'alert' as const, title: 'Checked-out stays with lines still to bill' }] : []),
  ];

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(() => { void load(); })];
  const filtersApplied = (from !== monthStart() ? 1 : 0) + (payer ? 1 : 0) + (search.trim() ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Invoices"
      description="Checkout invoices — one per payer of a stay, the posted lines added up. Issue what checkout left, view and print."
      icon={<IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (propertyId ? 1 : 0) + filtersApplied }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label={<>Property <span className="text-danger">*</span></>} isAsync loadOptions={loadPropertyOptions}
                  defaultOptions cacheOptions={false} isClearable={false} value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')} placeholder="Select property..." size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Invoice date from / to</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input type="date" className="form-input" style={{ fontSize: '0.8125rem', flex: 1, minWidth: 0 }} value={from} onChange={(e) => setFrom(e.target.value)} />
                  <input type="date" className="form-input" style={{ fontSize: '0.8125rem', flex: 1, minWidth: 0 }} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect label="Payer" options={PAYER_OPTIONS} value={payer} onChange={(v) => setPayer((v as string) || '')}
                  isClearable={false} size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Search</label>
                <input type="text" className="form-input" style={{ fontSize: '0.8125rem' }} value={search}
                  placeholder="Invoice no., booking, payer, guest, GSTIN" onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStart()); setTo(iso(new Date())); setPayer(''); setSearch(''); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody>
        {!propertyId ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property in the filters.</div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {section('pending', 'To invoice', pending.length, pending.length ? 'warning' : 'default',
              pending.length ? 'Checked-out stays with lines still to bill' : 'Every checked-out stay is invoiced',
              <SyutGrid data={pending} columns={pendingColumns} isLoading={loading} emptyMessage="Nothing waiting to be invoiced." />)}
            {section('issued', 'Issued', issued.length, 'default',
              `${day(from)} – ${day(to)} · total ${formatCurrency(totals.total)}`,
              <SyutGrid data={issued} columns={issuedColumns} rowActions={issuedActions} isLoading={loading} enablePagination emptyMessage="No invoices in the period." />)}
          </div>
        )}
      </ReportBody>

      <IssueInvoicesModal isOpen={!!invoiceFor && !folioFor} bookingId={invoiceFor} onClose={() => setInvoiceFor(null)} onIssued={() => { void load(); }}
        onOpenFolio={setFolioFor} />
      {/* A late charge or a receipt after checkout (slice 7c): the folio, then back to the invoices. */}
      <NoteModal isOpen={!!noteFor} invoiceId={noteFor} allowedActions={actions}
        onClose={() => setNoteFor(null)} onIssued={() => { setNoteFor(null); void load(); }} />
      <FolioModal isOpen={!!folioFor} folioId={folioFor} allowedActions={inHouseActions()}
        onClose={() => setFolioFor(null)} onChanged={() => { void load(); }} />
    </EnterpriseScreenShell>
  );
};

export default InvoicesScreen;
